import React, { useEffect, useMemo, useState } from 'react';
import { BellRing, BellOff, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  scheduleReminder,
  cancelReminder,
  getReminders,
  subscribeReminders,
  computeFireAt,
} from '@/lib/pushReminders';

const LEAD_OPTIONS = [
  { label: '30 min before', value: 30 },
  { label: '60 min before', value: 60 },
  { label: '90 min before', value: 90 },
];

export default function PushReminder({ airportCode, arriveBy, dateStr }) {
  const [permission, setPermission] = useState('default');
  const [reminders, setReminders] = useState([]);
  const [leadMinutes, setLeadMinutes] = useState(30);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPermission(getNotificationPermission());
    setReminders(getReminders());
    return subscribeReminders(setReminders);
  }, []);

  const supported = isPushSupported();

  // Compute the would-be reminder ID for this airport + arriveBy slot
  const targetFireAt = useMemo(
    () => computeFireAt(arriveBy, dateStr, leadMinutes),
    [arriveBy, dateStr, leadMinutes]
  );
  const existing = useMemo(
    () => reminders.find((r) => r.airportCode === airportCode && r.arriveBy === arriveBy),
    [reminders, airportCode, arriveBy]
  );

  const isPast = targetFireAt !== null && targetFireAt <= Date.now();

  const handleSet = async () => {
    setLoading(true);
    try {
      let perm = permission;
      if (perm !== 'granted') {
        perm = await requestNotificationPermission();
        setPermission(perm);
      }
      if (perm !== 'granted') {
        toast.error('Browser notifications are blocked. Enable them in your browser settings.');
        return;
      }
      if (targetFireAt === null) {
        toast.error('Could not parse arrival time.');
        return;
      }
      if (targetFireAt <= Date.now()) {
        toast.error('Arrival time is too soon — pick a longer lead time or a future flight.');
        return;
      }
      scheduleReminder({
        airportCode,
        arriveBy,
        fireAt: targetFireAt,
        leadMinutes,
        body: `Head to ${airportCode} now to arrive by ${arriveBy}.`,
      });
      toast.success(`Reminder set — we'll notify you ${leadMinutes} min before.`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (existing) {
      cancelReminder(existing.id);
      toast.success('Reminder cancelled');
    }
  };

  if (!supported) {
    return (
      <div className="text-xs text-slate-400 text-center py-2 flex items-center justify-center gap-1.5">
        <BellOff className="w-3.5 h-3.5" />
        Push reminders not supported in this browser
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="text-xs text-slate-500 text-center py-2 bg-amber-50 border border-amber-100 rounded-md px-3">
        <BellOff className="w-3.5 h-3.5 inline mr-1.5 text-amber-500" />
        Notifications are blocked. Enable them in your browser site settings to set leave-now reminders.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium text-slate-700">Leave-now reminder</span>
        </div>
        {existing ? (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-600">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
              Set for {existing.leadMinutes} min before
            </span>
          </div>
        ) : (
          <select
            value={leadMinutes}
            onChange={(e) => setLeadMinutes(Number(e.target.value))}
            data-testid="reminder-lead-select"
            className="text-xs h-8 px-2 border border-slate-200 rounded bg-white text-slate-700"
          >
            {LEAD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="mt-3">
        {existing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            data-testid="cancel-reminder-btn"
            className="w-full border-slate-300 text-slate-600 hover:bg-slate-100"
          >
            <BellOff className="w-3.5 h-3.5 mr-1.5" />
            Cancel reminder
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handleSet}
            disabled={loading || isPast}
            data-testid="set-reminder-btn"
            className="w-full bg-blue-500 hover:bg-blue-600 text-white"
          >
            {loading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Setting…</>
            ) : isPast ? (
              'Arrival time has passed'
            ) : (
              <><BellRing className="w-3.5 h-3.5 mr-1.5" /> Notify me to leave</>
            )}
          </Button>
        )}
      </div>

      <p className="text-[11px] text-slate-400 mt-2">
        Uses your browser's native notifications. Keep this tab open in the background.
      </p>
    </div>
  );
}
